import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getPaymentOrderStatusApi } from "../api/DonationActions";

type PaymentStatus = "success" | "pending" | "failed";

const PaymentReturnPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const orderId = searchParams.get("order_id");

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<PaymentStatus>("pending");
  const [message, setMessage] = useState("Checking payment status...");
  const [orderData, setOrderData] = useState<any>(null);

  const fallbackLogo = "/assets/images/bharat_sangam.png";

  const platformName = useMemo(() => {
    return (
      orderData?.platform_name ||
      orderData?.company_name ||
      "Bharat Sangam"
    );
  }, [orderData]);

  const platformLogo = useMemo(() => {
    return orderData?.logo || fallbackLogo;
  }, [orderData]);

  const amountText = useMemo(() => {
    const amount = Number(
      orderData?.payable_amount ||
        orderData?.amount ||
        0
    );

    if (!amount) return null;

    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: orderData?.currency || "INR",
      minimumFractionDigits: 2,
    }).format(amount);
  }, [orderData]);


  const donationPostId = useMemo(() => {
    if (orderData?.reference_type !== "post") {
      return null;
    }

    return (
      orderData?.reference_id ||
      orderData?.post_id ||
      null
    );
  }, [orderData]);

  const handleContinue = () => {
    if (donationPostId) {
      navigate(`/donate/${donationPostId}`, {
        replace: true,
      });
      return;
    }

    navigate("/home", {
      replace: true,
    });
  };

  const statusConfig = useMemo(() => {
    if (status === "success") {
      return {
        icon: "✓",
        title: "Payment Successful",
        label: "Transaction completed",
        accent: "#22C55E",
        soft: "#DCFCE7",
        button: "#FACC15",
        buttonText: "#111827",
      };
    }

    if (status === "failed") {
      return {
        icon: "✕",
        title: "Payment Failed",
        label: "Transaction unsuccessful",
        accent: "#EF4444",
        soft: "#FEE2E2",
        button: "#2563EB",
        buttonText: "#FFFFFF",
      };
    }

    return {
      icon: "!",
      title: "Payment Pending",
      label: "Waiting for confirmation",
      accent: "#F59E0B",
      soft: "#FEF3C7",
      button: "#2563EB",
      buttonText: "#FFFFFF",
    };
  }, [status]);

  useEffect(() => {
    const checkPayment = async () => {
      if (!orderId) {
        setStatus("failed");
        setMessage("Payment order ID is missing.");
        setLoading(false);
        return;
      }

      try {
        const response = await getPaymentOrderStatusApi(orderId);
        const order = response?.data || response;

        setOrderData(order);

        const paymentStatus = String(
          order?.order_status ||
            order?.payment_status ||
            order?.cashfree_order_status ||
            "pending"
        ).toLowerCase();

       if (
  ["paid", "success", "successful"].includes(
    paymentStatus
  )
) {
  setStatus("success");

  setMessage(
    "Your donation payment was completed successfully."
  );

  // Clear saved retry order after successful donation payment.
  const postId =
    order?.reference_type === "post"
      ? order?.reference_id ||
        order?.post_id
      : null;

  if (postId) {
    sessionStorage.removeItem(
      `pending_donation_order_id_${postId}`
    );

    sessionStorage.removeItem(
      `pending_donation_form_${postId}`
    );
  }
} else if (
          ["failed", "cancelled", "expired"].includes(paymentStatus)
        ) {
          setStatus("failed");
          setMessage("Your payment could not be completed.");
        } else {
          setStatus("pending");
          setMessage("We are still waiting for payment confirmation.");
        }
      } catch (error) {
        console.error(error);
        setStatus("failed");
        setMessage("Unable to verify payment status.");
      } finally {
        setLoading(false);
      }
    };

    checkPayment();
  }, [orderId]);

  return (
    <div className="payment-result-page">

      <div className="payment-stage">
        <div className="mock-phone mock-phone-right">
          <div className="mock-phone-header center">
            <strong>Transaction Details</strong>
          </div>

          {loading ? (
            <div className="mock-status-area">
              <div className="loading-spinner" />
              <p>Checking payment status...</p>
            </div>
          ) : (
            <>
              <div className="mock-status-area">
                <div
                  className="status-circle"
                  style={{
                    backgroundColor: statusConfig.soft,
                    color: statusConfig.accent,
                  }}
                >
                  {statusConfig.icon}
                </div>

                <span className="status-label">{statusConfig.label}</span>

                <h1>{statusConfig.title}</h1>

                <p>{message}</p>

                {status === "success" && donationPostId && (
                  <p className="success-note">
                    Continue to the donation page to view the updated donation details.
                  </p>
                )}
              </div>

              <div className="transaction-summary">
                <div className="summary-row">
                  <span>Order Code</span>
                  <strong>{orderData?.order_code || "-"}</strong>
                </div>

                <div className="summary-row">
                  <span>Amount</span>
                  <strong>{amountText || "-"}</strong>
                </div>

                <div className="summary-row">
                  <span>Order ID</span>
                  <strong>{orderId || "-"}</strong>
                </div>

                {orderData?.paid_at && (
                  <div className="summary-row">
                    <span>Date & Time</span>
                    <strong>
                      {new Date(orderData.paid_at).toLocaleString("en-IN")}
                    </strong>
                  </div>
                )}
              </div>

              <div className="action-row">
                <button
                  type="button"
                  className="secondary-action"
                  onClick={() => navigate(-1)}
                >
                  Back
                </button>

                <button
                  type="button"
                  className="primary-action"
                  style={{
                    backgroundColor: statusConfig.button,
                    color: statusConfig.buttonText,
                  }}
                  onClick={handleContinue}
                >
                  {status === "success" && donationPostId
                    ? "Back to Donation"
                    : "Go to Home"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        .payment-result-page {
          position: relative;
          min-height: 100vh;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          background:
            radial-gradient(circle at 15% 20%, rgba(255,255,255,0.95), transparent 28%),
            linear-gradient(135deg, #f6f7fb 0%, #eef1f7 45%, #f8f9fc 100%);
          font-family: Inter, system-ui, sans-serif;
        }

        .payment-stage {
          position: relative;
          width: min(420px, 100%);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .mock-phone {
          position: relative;
          width: 100%;
          min-height: 610px;
          padding: 24px;
          border-radius: 34px;
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          box-shadow: 0 28px 70px rgba(15, 23, 42, 0.16);
        }

        .mock-phone-right {
          position: relative;
          right: auto;
          transform: none;
          z-index: 2;
        }

        .mock-phone-header {
          display: flex;
          align-items: center;
          gap: 12px;
          color: #1f2937;
          font-size: 16px;
          margin-bottom: 24px;
        }

        .mock-phone-header.center {
          justify-content: center;
        }

        .mock-phone-header span {
          font-size: 34px;
          line-height: 1;
          color: #6b7280;
        }








        .mock-status-area {
          text-align: center;
          padding: 22px 8px 12px;
        }

        .status-circle {
          width: 88px;
          height: 88px;
          margin: 0 auto 16px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 44px;
          font-weight: 800;
        }

        .status-label {
          display: block;
          color: #6b7280;
          font-size: 12px;
          margin-bottom: 8px;
        }

        .mock-status-area h1 {
          margin: 0;
          color: #111827;
          font-size: 26px;
          font-weight: 800;
        }

        .mock-status-area p {
          margin: 12px auto 0;
          max-width: 260px;
          color: #6b7280;
          font-size: 13px;
          line-height: 1.6;
        }

        .success-note {
          margin-top: 8px !important;
          color: #16a34a !important;
          font-size: 12px !important;
          font-weight: 600;
        }

        .transaction-summary {
          margin-top: 20px;
          padding: 18px;
          border-radius: 18px;
          background: #f8fafc;
          border: 1px solid #e5e7eb;
        }

        .summary-row {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 16px;
          padding: 10px 0;
          border-bottom: 1px dashed #e5e7eb;
          font-size: 13px;
        }

        .summary-row:last-child {
          border-bottom: none;
        }

        .summary-row span {
          color: #6b7280;
        }

        .summary-row strong {
          color: #111827;
          text-align: right;
          word-break: break-word;
        }

        .action-row {
          display: flex;
          gap: 12px;
          margin-top: 22px;
        }

        .action-row button {
          flex: 1;
          min-height: 46px;
          border-radius: 13px;
          font-size: 13px;
          font-weight: 800;
          cursor: pointer;
          transition: transform 0.2s ease, opacity 0.2s ease;
        }

        .action-row button:hover {
          transform: translateY(-1px);
        }

        .secondary-action {
          border: 1px solid #d1d5db;
          background: white;
          color: #374151;
        }

        .primary-action {
          border: none;
        }

        .loading-spinner {
          width: 44px;
          height: 44px;
          margin: 70px auto 16px;
          border-radius: 50%;
          border: 4px solid #e5e7eb;
          border-top-color: #2563eb;
          animation: spin 0.9s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        @media (max-width: 900px) {
          .payment-stage {
            min-height: auto;
            display: block;
          }

          .mock-phone {
            position: relative;
            width: min(380px, 100%);
            min-height: auto;
            margin: 0 auto;
            transform: none;
          }

          .mock-phone-right {
            right: auto;
          }
        }

        @media (max-width: 480px) {
          .payment-result-page {
            padding: 16px 12px;
          }

          .mock-phone {
            padding: 20px 16px;
            border-radius: 24px;
          }

          .mock-status-area h1 {
            font-size: 23px;
          }

          .action-row {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
};

export default PaymentReturnPage;
